<script lang="ts" setup>
import { useHead } from '#app';
import { NuxtLoadingIndicator, NuxtPage } from '#components';
import { onMounted, ref } from 'vue';
import { stringifyQuery, useRoute, useRouter } from 'vue-router';

import '@sigma/stylebook/main.scss';
import MainHeader from '@/components/MainHeader.vue';
import { LanguageContext } from '@nzyme/i18n/LanguageContext.js';
import { translateToString } from '@nzyme/i18n/translateToString.js';
import { getObjectKeys } from '@nzyme/utils/getObjectKeys.js';
import { useService } from '@nzyme/vue-ioc/useService.js';
import { ModalHost } from '@nzyme/vue/modal/ModalHost.js';
import { ModalService } from '@nzyme/vue/modal/ModalService.js';
import { useScrollLock } from '@nzyme/vue/useScrollLock.js';
import { getPageHead } from '@sigma/client/utils/getPageHead.js';
import { getRouteKey } from '@sigma/client/utils/getRouteKey.js';
import { CountryContext } from '@sigma/common/i18n/CountryContext.js';
import { formatLocale } from '@sigma/common/i18n/Locale.js';
import { getLocalizedRouteName } from '@sigma/common/routes/getLocalizedRouteName.js';
import { getAppUrl, PATIENT_APP } from '@sigma/env/apps.js';
import { stripMarketingParams } from '@sigma/patient-common/landing/stripMarketingParams.js';
import ConfigProvider from '@sigma/ui/ConfigProvider.js';
import type { SerializableHead } from '@unhead/vue';

import * as l from './app.loc.js';
import { ChatListStore } from './chat/ChatListStore.js';

const route = useRoute();
const router = useRouter();
const modalService = useService(ModalService);
const langContext = useService(LanguageContext);
const countryContext = useService(CountryContext);
const chatListStore = useService(ChatListStore);
const baseUrl = getAppUrl(PATIENT_APP);

const menuOpen = ref(false);

useScrollLock({ condition: menuOpen });

onMounted(chatListStore.reload);

useHead(() => {
  const lang = langContext();
  const links: SerializableHead['link'] = [];

  // Canonical excludes marketing params so campaign-tagged landing variants
  // (e.g. /us?utm_campaign=glp1) don't index as duplicates of the clean page.
  const canonicalQuery = stringifyQuery(stripMarketingParams(route.query));
  links.push({
    rel: 'canonical',
    href: `${baseUrl}${route.path}${canonicalQuery ? `?${canonicalQuery}` : ''}`,
  });

  if (typeof route.meta.def.path === 'object') {
    for (const routeLang of getObjectKeys(route.meta.def.path)) {
      if (routeLang === lang) {
        continue;
      }

      const routeLocale = formatLocale(countryContext(), routeLang);
      const routeName = getLocalizedRouteName(route.meta.def.name, routeLocale);
      const routeResolved = router.resolve({ name: routeName, params: route.params });

      links.push({
        rel: 'alternate',
        hreflang: routeLang,
        href: `${baseUrl}${routeResolved.href}`,
      });
    }
  }

  return {
    htmlAttrs: { lang },
    ...getPageHead({
      title: translateToString(l.title, lang),
      description: translateToString(l.description, lang),
      noindex: route.meta.seo === false,
    }),
    link: links,
  };
});
</script>

<template>
  <NuxtLoadingIndicator color="#000" />
  <div :class="css.app">
    <ConfigProvider>
      <MainHeader
        v-model:menu-open="menuOpen"
        :class="css.header"
      />

      <NuxtLayout :name="route.meta.layout">
        <NuxtPage
          :page-key="getRouteKey(route)"
          :inert="modalService.modals.length > 0 || menuOpen"
        />
      </NuxtLayout>

      <ModalHost />
    </ConfigProvider>
  </div>
</template>

<style lang="scss">
html {
  scroll-behavior: smooth;
  scroll-padding-bottom: $spacing-64;
  scroll-padding-top: $main-header-height + $spacing-16;
}

#__nuxt {
  display: flex;
  flex-direction: column;
  flex: 1;
  z-index: 0;
  background-color: $color-doctor-50;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity ease-out 0.15s;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>

<style lang="scss" module="css">
.app {
  display: flex;
  flex-direction: column;
  flex: 1;
}

.header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
}
</style>
